import { useEffect, useRef } from "react";
import type { PlanEvent, Task, TaskId, TaskStatus, ViewMode } from "../../../shared/types";
import { CATEGORY_LABELS } from "../../../shared/types";
import { Overlay } from "../../../ui/atoms/Overlay";
import { emitNavigateToDate, emitHighlightTask } from "../../../lib/navigation-bus";

interface SearchModalProps {
  readonly query: string;
  readonly onQueryChange: (q: string) => void;
  readonly results: { tasks: Task[]; events: PlanEvent[] };
  readonly onClose: () => void;
  readonly onNavigate: (view: ViewMode) => void;
  readonly onUpdateTaskStatus?: (id: TaskId, status: TaskStatus) => void;
}

const STATUS_OPTIONS: ReadonlyArray<{ value: TaskStatus; label: string; color: string }> = [
  { value: "todo", label: "À faire", color: "text-ink-muted hover:bg-surface-alt" },
  { value: "doing", label: "En cours", color: "text-amber-500 hover:bg-amber-500/10" },
  { value: "done", label: "Fait", color: "text-emerald-500 hover:bg-emerald-500/10" },
];

export function SearchModal({
  query,
  onQueryChange,
  results,
  onClose,
  onNavigate,
  onUpdateTaskStatus,
}: SearchModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input on mount — delayed for mobile virtual keyboard support
  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Overlay open onClose={onClose} center={false}>
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-12 md:pt-24 px-3 md:px-4 pointer-events-none">
        <div className="pointer-events-auto relative w-full max-w-lg bg-surface border border-border rounded-2xl shadow-xl flex flex-col max-h-[70dvh] md:max-h-96 animate-scale-in overflow-hidden">
          <input
            ref={inputRef}
            type="search"
            inputMode="search"
            enterKeyHint="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Rechercher tâches et événements..."
            className="w-full px-4 py-3 text-base md:text-sm bg-transparent border-b border-border
              focus:outline-none"
            autoFocus
          />
          <div className="overflow-y-auto flex-1 p-2 scrollbar-custom">
            {results.tasks.length === 0 &&
              results.events.length === 0 &&
              query.length > 0 && (
                <p className="text-sm text-ink-faint text-center py-4">
                  Aucun résultat.
                </p>
              )}
            {results.tasks.length > 0 && (
              <div className="px-3 pt-2 pb-1">
                <span className="text-[10px] font-semibold text-ink-faint uppercase tracking-wider">Tâches</span>
              </div>
            )}
            {results.tasks.map((task) => (
              <div
                key={task.id}
                className="flex items-center gap-2 px-3 py-2 hover:bg-surface-alt rounded-lg group"
              >
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onNavigate("tasks");
                    setTimeout(() => emitHighlightTask(task.id), 100);
                  }}
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="font-medium text-sm text-ink truncate">{task.title}</div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs text-ink-muted">
                      {CATEGORY_LABELS[task.category]}{task.dueDate ? ` — ${task.dueDate}` : ""}
                    </span>
                    {task.tags.filter((t) => t.toLowerCase().includes(query.toLowerCase())).map((tag) => (
                      <span key={tag} className="px-1.5 py-0.5 text-[10px] bg-accent/10 text-accent rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </button>
                {onUpdateTaskStatus && (
                  <div className="flex gap-0.5 shrink-0 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                    {STATUS_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onUpdateTaskStatus(task.id, opt.value);
                        }}
                        className={`px-2 py-1 text-[10px] font-semibold rounded-md transition-colors ${
                          task.status === opt.value
                            ? `${opt.color} bg-surface-alt ring-1 ring-border`
                            : `text-ink-faint ${opt.color}`
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {results.events.length > 0 && (
              <div className="px-3 pt-2 pb-1">
                <span className="text-[10px] font-semibold text-ink-faint uppercase tracking-wider">Événements</span>
              </div>
            )}
            {results.events.map((event) => (
              <button
                key={event.id}
                type="button"
                onClick={() => {
                  onClose();
                  onNavigate("calendar");
                  // Delay to let calendar mount before navigating to date
                  setTimeout(() => emitNavigateToDate(event.date), 100);
                }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-surface-alt
                  focus:outline-none focus:bg-surface-alt rounded-lg"
              >
                <div className="font-medium text-ink">{event.title}</div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-xs text-ink-muted">
                    Événement — {event.date} à {event.startTime}
                  </span>
                  {event.tags.filter((t) => t.toLowerCase().includes(query.toLowerCase())).map((tag) => (
                    <span key={tag} className="px-1.5 py-0.5 text-[10px] bg-accent/10 text-accent rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </Overlay>
  );
}
